import '@fontsource/sora/400.css';
import '@fontsource/sora/600.css';
import '@fontsource/sora/700.css';
import '@fontsource/sora/800.css';
import 'highcharts/esm/highcharts';

import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import I18NProvider from '@risksmart-app/components/src/providers/I18nProvider';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { buildSentryEnvironment } from '@risksmart-app/shared/src/utils/sentry';
import * as Sentry from '@sentry/react';
import { StrictMode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { App } from './App';
import { initialiseWebFeatureFlags } from './feature-flags/openfeature';
import { initProductlane } from './productlane';
import { handleSentryBeforeSend, sentryIgnoreErrors } from './sentryUtils';

if (
  getEnv('REACT_APP_ENVIRONMENT') === 'dev-local' ||
  getEnv('REACT_APP_ENVIRONMENT') === 'dev-cloud' ||
  getEnv('REACT_APP_ENVIRONMENT') === 'staging'
) {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

const sentryBaseEnvironment = getEnv('REACT_APP_ENVIRONMENT');
const sentryRegionId = getEnv('REACT_APP_REGION_ID', true);
const sentryEnvironment = buildSentryEnvironment(
  sentryBaseEnvironment,
  sentryRegionId
);

// TODO: lots of other settings to try out later!!!
Sentry.init({
  dsn: 'https://b2780d88dbf149ea915846fa910e9d6f@o4505232398745600.ingest.sentry.io/4505232587489280',
  environment: sentryEnvironment,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    Sentry.replayIntegration({
      // Additional SDK configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.0,
  sampleRate: 1.0,
  tracesSampleRate: 1.0,
  ignoreErrors: sentryIgnoreErrors,
  beforeSend: handleSentryBeforeSend,
  enabled: getEnv('REACT_APP_ENABLE_SENTRY', true) == 'true',
});

// Register the OpenFeature provider before React mounts (after Sentry.init so
// a misconfigured provider is reported) — flag hooks see the real provider on
// first render instead of defaults-then-flip.
initialiseWebFeatureFlags();

// Initialise the Productlane support widget (no-op when disabled via env).
initProductlane();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <I18NProvider>
      <App />
    </I18NProvider>
  </StrictMode>
);
